import React from "react";
import { Link, Route, Routes, useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { api, inr, fileUrl, openPdf, errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Package, Heart, LogOut, ShoppingBag, CreditCard, Loader2, RotateCcw, Truck, ExternalLink, Check } from "lucide-react";
import { ReturnForm, Timeline } from "@/pages/AdminOrders";
import { ReturnPill } from "@/pages/AdminReturns";
import { Pagination, usePaged } from "@/components/Pagination";
import { payWithRazorpay } from "@/lib/razorpay";

const STATUS_LABEL = { awaiting_payment: "Awaiting payment", payment_failed: "Payment failed", cod_pending: "Pay on delivery" };
const label = (s) => STATUS_LABEL[s] || (s || "").replace(/_/g, " ");

export default function Account() {
  const { user, logout } = useAuth();
  if (user === null) return <div className="p-20 text-center text-neutral-500">Loading…</div>;
  if (!user) return <div className="p-20 text-center">Please <Link to="/login?redirect=/account" className="underline">sign in</Link>.</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 grid lg:grid-cols-[240px_1fr] gap-10">
      <aside>
        <div className="mb-8">
          <div className="overline mb-1">Signed in as</div>
          <div className="font-semibold">{user.name}</div>
          <div className="text-xs text-neutral-500">{user.email}</div>
        </div>
        <nav className="space-y-1">
          <SideLink to="/account" icon={ShoppingBag}>Overview</SideLink>
          <SideLink to="/account/orders" icon={Package}>Orders</SideLink>
          <SideLink to="/account/returns" icon={RotateCcw}>Returns</SideLink>
          <SideLink to="/account/wishlist" icon={Heart}>Wishlist</SideLink>
          <button onClick={logout} className="flex items-center gap-2 py-2 text-sm text-[#991B1B] hover:underline w-full" data-testid="acct-logout"><LogOut size={14}/>Sign out</button>
        </nav>
      </aside>
      <div>
        <Routes>
          <Route index element={<Overview/>}/>
          <Route path="orders" element={<Orders/>}/>
          <Route path="orders/:id" element={<OrderDetail/>}/>
          <Route path="wishlist" element={<Wishlist/>}/>
          <Route path="returns" element={<MyReturns/>}/>
        </Routes>
      </div>
    </div>
  );
}
function SideLink({ to, icon: Icon, children }) {
  return <Link to={to} className="flex items-center gap-2 py-2 text-sm hover:text-[#0E4D34]"><Icon size={14}/>{children}</Link>;
}
function Overview() {
  const { data: orders } = useQuery({ queryKey: ["my-orders"], queryFn: () => api.get("/orders").then(r => r.data) });
  return (
    <div>
      <h1 className="h2-editorial mb-6">Welcome</h1>
      <div className="grid sm:grid-cols-3 gap-4">
        <Card label="Orders" value={orders?.length || 0}/>
        <Card label="In Transit" value={orders?.filter(o => ["shipped", "packed"].includes(o.status)).length || 0}/>
        <Card label="Delivered" value={orders?.filter(o => o.status === "delivered").length || 0}/>
      </div>
    </div>
  );
}
function Card({ label, value }) {
  return <div className="bg-white border p-6"><div className="overline mb-2">{label}</div><div className="font-serif text-3xl text-[#0E4D34]">{value}</div></div>;
}
function Orders() {
  const { data } = useQuery({ queryKey: ["my-orders"], queryFn: () => api.get("/orders").then(r => r.data) });
  const pg = usePaged(data || [], 10, { scrollTop: true });
  return (
    <div>
      <h1 className="h2-editorial mb-6">My Orders</h1>
      <div className="space-y-3">
        {(data || []).length === 0 && <p className="text-neutral-500">No orders yet.</p>}
        {pg.items.map(o => (
          <Link key={o.id} to={`/account/orders/${o.id}`} className="block bg-white border p-4 hover:border-[#0E4D34]" data-testid={`order-${o.order_number}`}>
            <div className="flex justify-between items-start flex-wrap gap-2">
              <div>
                <div className="font-semibold">{o.order_number}</div>
                <div className="text-xs text-neutral-500">{new Date(o.created_at).toLocaleDateString()}</div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-[#0E4D34]">{inr(o.total)}</div>
                <span className={`text-xs uppercase tracking-widest px-2 py-1 ${["awaiting_payment", "payment_failed"].includes(o.status) ? "bg-[#FFF7E0] text-[#8a6d00]" : "bg-[#E8F3EE] text-[#0E4D34]"}`}>{label(o.status)}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
      <Pagination {...pg.props} label="orders"/>
    </div>
  );
}
function OrderDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data, refetch } = useQuery({ queryKey: ["order", id], queryFn: () => api.get(`/orders/${id}`).then(r => r.data) });
  const [paying, setPaying] = React.useState(false);
  if (!data) return <div>Loading...</div>;
  const unpaidOnline = data.payment_method === "razorpay" && ["pending", "failed"].includes(data.payment_status) && !["cancelled", "returned"].includes(data.status);
  const payNow = async () => {
    setPaying(true);
    try {
      const r = await api.post(`/orders/${data.id}/pay`);
      const res = await payWithRazorpay(data.id, r.data);
      if (res.status === "paid") toast.success("Payment received — your order is confirmed!");
      else if (res.status === "failed") toast.error(res.message || "Payment failed. Please try again.");
      refetch(); qc.invalidateQueries({ queryKey: ["my-orders"] });
    } catch (e) { toast.error(errorMessage(e)); } finally { setPaying(false); }
  };
  return (
    <div>
      <h1 className="h2-editorial mb-2">Order {data.order_number}</h1>
      <div className="text-sm text-neutral-500 mb-6">{new Date(data.created_at).toLocaleString()}</div>
      {unpaidOnline && (
        <div className="mb-6 rounded-2xl border border-[#E9D8A6] bg-[#FFF7E0] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="pay-now-box">
          <div className="text-sm"><div className="font-semibold">This order is waiting for payment</div><div className="text-neutral-600">Complete the payment to confirm your order. Items are reserved only after payment.</div></div>
          <button onClick={payNow} disabled={paying} className="btn-silk rounded-[10px] whitespace-nowrap disabled:opacity-60" data-testid="pay-now">
            {paying ? <Loader2 size={14} className="animate-spin mr-2"/> : <CreditCard size={14} className="mr-2"/>}Pay {inr(data.total)} now
          </button>
        </div>
      )}
      <Progress status={data.status}/>
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border p-4">
          <div className="overline mb-2">Status</div>
          <div className="font-semibold uppercase text-[#0E4D34]">{label(data.status)}</div>
          <div className="text-sm mt-1">Payment: <span className="font-semibold capitalize">{label(data.payment_status)}</span>{data.payment_method === "razorpay" ? " (online)" : " (cash on delivery)"}</div>
          {data.return_status && <div className="text-sm mt-1">Return: <span className="font-semibold capitalize">{label(data.return_status)}</span></div>}
        </div>
        <div className="bg-white border p-4">
          <div className="overline mb-2">Ship to</div>
          <div className="text-sm">{data.address.full_name}<br/>{data.address.line1}, {data.address.city}, {data.address.state} {data.address.pincode}<br/>{data.address.phone}</div>
        </div>
      </div>
      <div className="bg-white border p-4 mb-4">
        <div className="overline mb-3">Items</div>
        {data.items.map((it, i) => (
          <div key={i} className="flex justify-between py-2 border-b last:border-0"><span>{it.name} × {it.quantity}</span><span>{inr(it.price * it.quantity)}</span></div>
        ))}
        {data.discount > 0 && <div className="pt-2 flex justify-between text-sm text-neutral-600"><span>Discount{data.coupon_code ? ` (${data.coupon_code})` : ""}</span><span>−{inr(data.discount)}</span></div>}
        {data.tax > 0 && <div className="pt-2 flex justify-between text-sm text-neutral-600"><span>GST</span><span>{inr(data.tax)}</span></div>}
        <div className="pt-2 flex justify-between text-sm text-neutral-600"><span>Shipping</span><span>{data.shipping ? inr(data.shipping) : "Free"}</span></div>
        <div className="pt-3 flex justify-between font-bold text-[#0E4D34]"><span>Total</span><span>{inr(data.total)}</span></div>
      </div>
      {(data.tracking_number || (data.shipping_info || {}).awb) && <Tracking orderId={data.id}/>}
      <OrderReturns order={data} onChanged={refetch}/>
      <button type="button" onClick={() => openPdf(`/orders/${data.id}/invoice`).catch(e => toast.error(errorMessage(e)))} className="btn-outline-silk" data-testid="download-invoice">Download Invoice</button>
    </div>
  );
}

const STEPS = [["pending", "Placed"], ["confirmed", "Confirmed"], ["packed", "Packed"], ["shipped", "Shipped"], ["delivered", "Delivered"]];
function Progress({ status }) {
  const idx = STEPS.findIndex(([k]) => k === status);
  if (idx < 0) return null;
  return (
    <div className="bg-white border p-4 sm:p-5 mb-6" data-testid="order-progress">
      <div className="flex items-center">
        {STEPS.map(([k, l], i) => (
          <React.Fragment key={k}>
            <div className="flex flex-col items-center gap-1.5 min-w-[52px]">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${i <= idx ? "bg-[#0E4D34] text-white" : "bg-neutral-100 text-neutral-400"}`}>{i < idx || status === "delivered" ? <Check size={14}/> : i + 1}</span>
              <span className={`text-[10px] sm:text-xs ${i <= idx ? "text-[#0E4D34] font-semibold" : "text-neutral-400"}`}>{l}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mb-5 ${i < idx ? "bg-[#0E4D34]" : "bg-neutral-200"}`}/>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function Tracking({ orderId }) {
  const { data } = useQuery({ queryKey: ["tracking", orderId], queryFn: () => api.get(`/orders/${orderId}/tracking`).then(r => r.data) });
  if (!data || !data.awb) return null;
  return (
    <div className="bg-white border p-4 sm:p-5 mb-4" data-testid="order-tracking">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Truck size={14}/>Shipment</div>
          <div className="text-sm">{data.courier || "Courier"} · <span className="font-mono">{data.awb}</span></div>
          {data.current && <div className="text-sm font-semibold text-[#0E4D34] mt-1 capitalize">{String(data.current).toLowerCase()}</div>}
          {data.etd && data.status !== "delivered" && <div className="text-xs text-neutral-500 mt-1">Expected delivery: {String(data.etd)}</div>}
        </div>
        {data.tracking_url && <a href={data.tracking_url} target="_blank" rel="noreferrer" className="btn-outline-silk rounded-[10px] text-xs inline-flex items-center gap-1.5"><ExternalLink size={12}/>Track on courier site</a>}
      </div>
      {(data.events || []).length > 0 && <div className="mt-4"><Timeline events={data.events}/></div>}
    </div>
  );
}

function OrderReturns({ order, onChanged }) {
  const qc = useQueryClient();
  const { data: mine, refetch } = useQuery({ queryKey: ["my-returns"], queryFn: () => api.get("/returns").then(r => r.data) });
  const [open, setOpen] = React.useState(false);
  const list = (mine || []).filter(r => r.order_id === order.id);
  if (order.status !== "delivered" && !list.length) return null;
  return (
    <div className="bg-white border p-4 sm:p-5 mb-4" data-testid="order-returns">
      <div className="overline mb-3 flex items-center gap-2"><RotateCcw size={14}/>Returns</div>
      {list.map(r => (
        <div key={r.id} className="flex items-center justify-between gap-3 border p-3 mb-2 text-sm flex-wrap">
          <div><span className="font-mono">{r.return_number}</span> · {r.items.map(i => `${i.name} ×${i.quantity}`).join(", ")}</div>
          <ReturnPill status={r.status}/>
        </div>
      ))}
      {order.status === "delivered" && (open
        ? <div className="mt-2"><ReturnForm order={order} onDone={() => { setOpen(false); refetch(); onChanged(); qc.invalidateQueries({ queryKey: ["returnable", order.id] }); }} onCancel={() => setOpen(false)}/></div>
        : <button onClick={() => setOpen(true)} className="btn-outline-silk rounded-[10px] mt-1" data-testid="start-return">Return or exchange items</button>)}
    </div>
  );
}

const RETURN_STEP_TEXT = {
  requested: "We have received your request and will reply within 24 hours.",
  approved: "Approved — our courier will contact you to pick the parcel up.",
  pickup_scheduled: "Pickup booked — please keep the parcel packed with tags.",
  in_transit: "Your parcel is on its way back to us.",
  received: "We received the parcel and are processing your refund / exchange.",
  refunded: "Refund sent. Online payments reach your account in 5–7 working days.",
  rejected: "This return could not be accepted — see the note or message us on WhatsApp.",
  closed: "This return is closed.",
};

function MyReturns() {
  const { data } = useQuery({ queryKey: ["my-returns"], queryFn: () => api.get("/returns").then(r => r.data) });
  const pg = usePaged(data || [], 10);
  return (
    <div data-testid="my-returns">
      <h1 className="h2-editorial mb-2">Returns</h1>
      <p className="text-sm text-neutral-500 mb-6">To return a piece, open the delivered order in <Link to="/account/orders" className="underline">My Orders</Link> and choose "Return or exchange items".</p>
      <div className="space-y-3">
        {(data || []).length === 0 && <p className="text-neutral-500">No returns.</p>}
        {pg.items.map(r => (
          <div key={r.id} className="bg-white border p-4">
            <div className="flex justify-between items-start gap-2 flex-wrap">
              <div><div className="font-semibold font-mono">{r.return_number}</div><div className="text-xs text-neutral-500">Order <Link to={`/account/orders/${r.order_id}`} className="underline">{r.order_number}</Link> · {new Date(r.created_at).toLocaleDateString()}</div></div>
              <ReturnPill status={r.status}/>
            </div>
            <div className="text-sm mt-2">{r.items.map(i => `${i.name} ×${i.quantity}`).join(", ")}</div>
            <div className="text-xs text-neutral-600 mt-1">{RETURN_STEP_TEXT[r.status]}</div>
            {r.shipment?.awb && <div className="text-xs mt-1">Pickup AWB <span className="font-mono">{r.shipment.awb}</span> · <a href={r.shipment.tracking_url} target="_blank" rel="noreferrer" className="underline">track</a></div>}
            {r.refund && <div className="text-xs mt-1 text-[#0E4D34]">Refunded {inr(r.refund.amount)} · ref {r.refund.reference}</div>}
          </div>
        ))}
      </div>
      <Pagination {...pg.props} label="returns"/>
    </div>
  );
}
function Wishlist() {
  const { data, refetch } = useQuery({ queryKey: ["wishlist"], queryFn: () => api.get("/wishlist").then(r => r.data) });
  const { addToCart } = useAuth();
  const pg = usePaged(data || [], 12, { scrollTop: true });
  return (
    <div>
      <h1 className="h2-editorial mb-6">Wishlist</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {(data || []).length === 0 && <p className="text-neutral-500 col-span-full">Nothing saved yet.</p>}
        {pg.items.map(p => (
          <div key={p.id} className="bg-white border p-4">
            <img src={fileUrl((p.images || [])[0])} className="w-full aspect-[3/4] object-cover mb-3"/>
            <div className="font-serif text-lg">{p.name}</div>
            <div className="text-[#0E4D34] font-semibold mb-3">{inr(p.price)}</div>
            <div className="flex gap-2">
              <button onClick={() => addToCart(p)} className="btn-silk flex-1">Add to Bag</button>
              <button onClick={async () => { await api.delete(`/wishlist/${p.id}`); refetch(); }} className="btn-outline-silk">Remove</button>
            </div>
          </div>
        ))}
      </div>
      <Pagination {...pg.props} label="saved pieces"/>
    </div>
  );
}
